import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, firstValueFrom } from 'rxjs';
import {
  ChangePasswordModel,
  CreateUserModel,
  UpdateUserCompanyModel,
  UpdateUserRoleModel,
  UserModel,
  UserResponseModel,
  UserResponsibilityModel,
  UserResponsibilityResponseModel,
} from '../models/user.model';
import {
  errorHandling,
  isNullOrUndefined,
} from '../../shared/utils/checker.utils';
import {
  parseUser,
  parseUserResponsibilityModel,
} from '../../shared/utils/parser.utils';
import { environment } from '../../../environment/environment';
import { ApiResponse } from '../models/api-response.model';
import { appendNumber } from '../../shared/utils/form-data.utils';

/** Provides user operations for the application. */
@Injectable({
  providedIn: 'root',
})
export class User {
  /** Sends HTTP requests to the user API. */
  private readonly http = inject(HttpClient);
  /** Base URL used to construct user API endpoint URLs. */
  private readonly serviceUrl = environment.authority_short + 'dashboard/user/';

  /** Currently authenticated user cached in memory, or null when not set. */
  private user: UserModel = null;

  /**
   * Sends a POST request to the user backend with centralized error handling.
   * @param endpoint Backend PHP endpoint name.
   * @param body Request payload.
   * @returns Parsed response object from the backend.
   */
  private async postRequest<T>(
    endpoint: string,
    body: FormData | Record<string, never>
  ): Promise<T> {
    return firstValueFrom(
      this.http
        .post<T>(this.serviceUrl + endpoint, body)
        .pipe(catchError(error => errorHandling(error)))
    );
  }

  /**
   * Stores the currently authenticated user in service state.
   * @param user User model that should be cached in memory.
   * @returns Nothing.
   */
  public setUser(user: UserModel): void {
    this.user = user;
  }

  /**
   * Returns the currently cached user.
   * @returns Cached user model or null when no user is set.
   */
  public getUser(): UserModel {
    return this.user;
  }

  /**
   * Retrieves all users with their responsibilities.
   * @returns Parsed list of user responsibility models.
   */
  public async getAllUsers(): Promise<UserResponsibilityModel[]> {
    const response = await this.postRequest<
      ApiResponse<UserResponsibilityResponseModel[]>
    >('getAllUsers.php', {});

    console.debug('getAllUsers(): ', response);

    if (isNullOrUndefined(response.data)) {
      return [];
    }

    return response.data.map(user => parseUserResponsibilityModel(user));
  }

  /**
   * Retrieves one user by identifier.
   * @param idUser Identifier of the user.
   * @returns Parsed user model or null when no data is returned.
   */
  public async getUserById(idUser: number): Promise<UserModel> {
    const formData = new FormData();
    appendNumber(formData, 'id_user', idUser);

    const response = await this.postRequest<ApiResponse<UserResponseModel>>(
      'getUser.php',
      formData
    );

    console.debug('getUserById(): ', response);

    if (isNullOrUndefined(response.data)) {
      return null;
    }

    return parseUser(response.data);
  }

  /**
   * Creates a new user.
   * @param createUser Payload containing user creation data.
   * @returns Parsed created user model or null when no data is returned.
   */
  public async createUser(createUser: CreateUserModel): Promise<UserModel> {
    const formData = new FormData();
    formData.append('first_name', createUser.first_name);
    formData.append('last_name', createUser.last_name);
    formData.append('email', createUser.email);
    formData.append('password', createUser.password);
    formData.append('abbreviation', createUser.abbreviation);
    appendNumber(formData, 'id_role', createUser.id_role);
    appendNumber(formData, 'id_company', createUser.id_company);

    const response = await this.postRequest<ApiResponse<UserResponseModel>>(
      'createUser.php',
      formData
    );

    console.debug('createUser(): ', response);

    if (isNullOrUndefined(response.data)) {
      return null;
    }

    return parseUser(response.data);
  }

  /**
   * Sends a password reset email to a user.
   * @param email Email address that should receive the reset message.
   * @returns Backend response containing status details.
   */
  public async sendResetMail(email: string): Promise<ApiResponse<unknown>> {
    const formData = new FormData();
    formData.append('email', email);

    const response = await this.postRequest<ApiResponse<unknown>>(
      'sendResetMail.php',
      formData
    );

    console.debug('sendResetMail(): ', response);
    return response;
  }

  /**
   * Updates a user's password by using a reset token.
   * @param token Reset token provided by the reset flow.
   * @param password New password value.
   * @returns Backend response containing status details.
   */
  public async updatePasswordByToken(
    token: string,
    password: string
  ): Promise<ApiResponse<unknown>> {
    const formData = new FormData();
    formData.append('token', token);
    formData.append('password', password);

    const response = await this.postRequest<ApiResponse<unknown>>(
      'updatePasswordByToken.php',
      formData
    );

    console.debug('updatePasswordByToken(): ', response);
    return response;
  }

  /**
   * Changes a user's password by validating the current password.
   * @param changePassword Payload containing old and new password information.
   * @returns Backend response containing status details.
   */
  public async changePassword(
    changePassword: ChangePasswordModel
  ): Promise<ApiResponse<unknown>> {
    const formData = new FormData();
    appendNumber(formData, 'id_user', changePassword.userId);
    formData.append('password_old', changePassword.passwordOld);
    formData.append('password_new', changePassword.passwordNew);

    const response = await this.postRequest<ApiResponse<unknown>>(
      'updatePassword.php',
      formData
    );

    console.debug('changePassword(): ', response);
    return response;
  }

  /**
   * Updates the company assignment of a user.
   * @param updateUserCompany Payload containing user and company identifiers.
   * @returns Backend response containing status details.
   */
  public async updateUserCompany(
    updateUserCompany: UpdateUserCompanyModel
  ): Promise<ApiResponse<unknown>> {
    const formData = new FormData();
    appendNumber(formData, 'id_user', updateUserCompany.idUser);
    appendNumber(formData, 'id_company', updateUserCompany.idCompany);

    const response = await this.postRequest<ApiResponse<unknown>>(
      'updateUserCompany.php',
      formData
    );

    console.debug('updateUserCompany(): ', response);
    return response;
  }

  /**
   * Updates role assignments of a user.
   * @param updateUserRole Payload containing user and role identifiers.
   * @returns Backend response containing status details.
   */
  public async updateUserRole(
    updateUserRole: UpdateUserRoleModel
  ): Promise<ApiResponse<unknown>> {
    const formData = new FormData();
    appendNumber(formData, 'id_user', updateUserRole.idUser);

    if (!isNullOrUndefined(updateUserRole.idRole)) {
      appendNumber(formData, 'id_role', updateUserRole.idRole);
    }

    if (!isNullOrUndefined(updateUserRole.idRoleOnOff)) {
      appendNumber(formData, 'id_role_onoff', updateUserRole.idRoleOnOff);
    }

    const response = await this.postRequest<ApiResponse<unknown>>(
      'updateUserRole.php',
      formData
    );

    console.debug('updateUserRole(): ', response);
    return response;
  }
}
